// @workspace/plans — shared plan + feature matrix used by both API server
// and web client. Keep this package dependency-free so it can be imported
// from any environment.

// ---------------------------------------------------------------------------
// Plan types
// ---------------------------------------------------------------------------

/** Canonical plan keys */
export type Plan = "free" | "track" | "draft" | "enterprise";

/** Alias retained for backward-compat with imports from earlier code paths */
export type NewPlan = Plan;

/** Canonical order for entitlement ranking */
export const PLAN_ORDER: Plan[] = ["free", "track", "draft", "enterprise"];

// ---------------------------------------------------------------------------
// Plan metadata
// ---------------------------------------------------------------------------

export interface PlanMeta {
  key: Plan;
  name: string;
  /**
   * Monthly-equivalent platform fee (USD); null = "Custom".
   * Paid plans are annual-only, so for Track/Draft this is `annualPrice ÷ 12`
   * (Track 4167, Draft 8333) — kept around because `orgMonthlyRevenue` in
   * admin.ts uses it for MRR/ARR reporting. Free is 0; Enterprise is null.
   */
  monthlyPrice: number | null;
  /**
   * Annual platform fee total (USD, billed as one payment); null = "Custom".
   * Track: $50,000/yr. Draft: $100,000/yr.
   */
  annualPrice: number | null;
  /** Monthly equivalent of annual price (for display only); null = "Custom". */
  annualMonthlyEquivalent: number | null;
  /** Number of editor seats included in the platform fee */
  includedSeats: number;
  /** Per-seat monthly add-on price (USD) for seats beyond included; null = n/a */
  perSeatMonthly: number | null;
  /** Per-seat annual add-on equivalent monthly price (USD); null = n/a */
  perSeatAnnual: number | null;
  /** Monthly AI extractions included in platform fee; null = unlimited or custom */
  includedAiExtractions: number | null;
  /** Overage cost per AI extraction beyond included (USD); null = n/a */
  aiExtractionOverage: number | null;
  /** Monthly e-sign envelopes included in platform fee; null = unlimited or custom */
  includedEsignEnvelopes: number | null;
  /** Overage cost per e-sign envelope beyond included (USD); null = n/a */
  esignOverageRate: number | null;
  description: string;
  /** Minimum seat purchase for this plan */
  minSeats: number;
  /** Marketing summary bullets shown on the landing page */
  highlights: string[];
  /** Features NOT included (shown in comparison matrix) */
  notIncluded?: string[];
  /** Optional CTA override */
  ctaLabel?: string;
  /** Whether this plan supports self-serve checkout */
  selfServe: boolean;
}

export const PLAN_META: Record<Plan, PlanMeta> = {
  free: {
    key: "free",
    name: "Free",
    monthlyPrice: 0,
    annualPrice: 0,
    annualMonthlyEquivalent: 0,
    includedSeats: 1,
    perSeatMonthly: null,
    perSeatAnnual: null,
    includedAiExtractions: 5,
    aiExtractionOverage: null,
    includedEsignEnvelopes: 5,
    esignOverageRate: null,
    description: "14-day trial of Track included. Read-only repository up to 5 coaches.",
    minSeats: 1,
    highlights: [
      "1 admin seat",
      "Up to 5 coaches (read-only)",
      "Contract repository — view & track",
      "Insurance & compliance tracking",
      "Expiration alerts",
      "14-day Track trial",
    ],
    notIncluded: ["Contract drafting", "AI PDF import", "E-signature send"],
    ctaLabel: "Get Started Free",
    selfServe: false,
  },
  track: {
    key: "track",
    name: "Track",
    monthlyPrice: 4167,
    annualPrice: 50000,
    annualMonthlyEquivalent: 4167,
    includedSeats: 2,
    perSeatMonthly: 59,
    perSeatAnnual: 49,
    includedAiExtractions: 20,
    aiExtractionOverage: 4,
    includedEsignEnvelopes: 20,
    esignOverageRate: 3,
    description: "Coach directory, contract repository, renewals pipeline, and compliance tracking.",
    minSeats: 2,
    highlights: [
      "$50,000/yr · billed annually",
      "2 editor seats included",
      "Unlimited viewer seats",
      "Coach directory & contract repository",
      "Renewals pipeline",
      "Insurance & compliance tracking",
      "20 AI extractions/mo · $4 overage",
      "20 e-sign envelopes/mo · $3 overage",
      "50 active contracts",
    ],
    notIncluded: ["Contract drafting", "AI PDF import", "E-signature send", "Approval workflow"],
    ctaLabel: "Start Free Trial",
    selfServe: true,
  },
  draft: {
    key: "draft",
    name: "Draft",
    monthlyPrice: 8333,
    annualPrice: 100000,
    annualMonthlyEquivalent: 8333,
    includedSeats: 5,
    perSeatMonthly: 159,
    perSeatAnnual: 159,
    includedAiExtractions: 150,
    aiExtractionOverage: 6,
    includedEsignEnvelopes: 150,
    esignOverageRate: 4,
    description: "Everything in Track, plus full contract authoring, AI extraction, and e-signature.",
    minSeats: 5,
    highlights: [
      "$100,000/yr · billed annually",
      "5 editor seats included",
      "Unlimited viewer seats",
      "Contract drafting — new & renewal",
      "AI PDF extraction to draft",
      "Approval workflow & e-signature",
      "150 AI extractions/mo · $6 overage",
      "150 e-sign envelopes/mo · $4 overage",
      "250 active contracts",
    ],
    ctaLabel: "Start Free Trial",
    selfServe: true,
  },
  enterprise: {
    key: "enterprise",
    name: "Enterprise",
    monthlyPrice: null,
    annualPrice: null,
    annualMonthlyEquivalent: null,
    includedSeats: 25,
    perSeatMonthly: null,
    perSeatAnnual: 139,
    includedAiExtractions: 1000,
    aiExtractionOverage: null,
    includedEsignEnvelopes: 1000,
    esignOverageRate: null,
    description: "Everything in Draft, plus SSO, public API, clause library, and dedicated support.",
    minSeats: 25,
    highlights: [
      "Starts at $150,000/yr (annual only)",
      "25 editor seats · $139/seat/mo add-on",
      "1,000 active contracts · $30/contract/yr add-on",
      "1,000 AI extractions & e-sign/mo",
      "SSO / SAML",
      "Public REST API",
      "Clause library & role-based templates",
      "Dedicated CSM & SLA",
    ],
    ctaLabel: "Contact Sales",
    selfServe: false,
  },
};

// ---------------------------------------------------------------------------
// Feature matrix
// ---------------------------------------------------------------------------

export type FeatureKey =
  | "coaches.basic"
  | "coaches.bulk_import"
  | "comments.mentions"
  | "exports.csv"
  | "exports.scheduled"
  | "buyout.advanced"
  | "branding.custom"
  | "support.priority"
  | "auth.sso"
  | "api.public"
  | "contracts.draft"
  | "contracts.ai_import"
  | "esign.send"
  | "approvals.workflow";

export interface FeatureSpec {
  key: FeatureKey;
  /** Lowest plan that unlocks the feature */
  minPlan: Plan;
  /** Short human label */
  label: string;
  /** One-line marketing description */
  description: string;
}

export const FEATURES: Record<FeatureKey, FeatureSpec> = {
  "coaches.basic": {
    key: "coaches.basic",
    minPlan: "free",
    label: "Coach directory",
    description: "Centralized coach profiles with insurance + contract status.",
  },
  "coaches.bulk_import": {
    key: "coaches.bulk_import",
    minPlan: "track",
    label: "CSV imports",
    description: "Import coaches and contracts from a CSV.",
  },
  "comments.mentions": {
    key: "comments.mentions",
    minPlan: "track",
    label: "Team mentions",
    description: "@-mention teammates on contracts and coaches.",
  },
  "exports.csv": {
    key: "exports.csv",
    minPlan: "free",
    label: "CSV exports",
    description: "Download contracts and coach lists as CSV.",
  },
  "exports.scheduled": {
    key: "exports.scheduled",
    minPlan: "draft",
    label: "Scheduled exports",
    description: "Recurring CSV / PDF exports delivered to your inbox.",
  },
  "contracts.draft": {
    key: "contracts.draft",
    minPlan: "draft",
    label: "Contract drafting",
    description: "Create new contracts and renewals from scratch with templates.",
  },
  "contracts.ai_import": {
    key: "contracts.ai_import",
    minPlan: "draft",
    label: "AI PDF extraction",
    description: "Upload a contract PDF and extract structured data with AI.",
  },
  "esign.send": {
    key: "esign.send",
    minPlan: "draft",
    label: "E-signature send",
    description: "Send contracts for in-app or external e-signature.",
  },
  "approvals.workflow": {
    key: "approvals.workflow",
    minPlan: "draft",
    label: "Approval workflow",
    description: "Route contracts through multi-step approval chains.",
  },
  "buyout.advanced": {
    key: "buyout.advanced",
    minPlan: "draft",
    label: "Advanced buyout calculator",
    description: "Model termination scenarios with mitigation + schedules.",
  },
  "branding.custom": {
    key: "branding.custom",
    minPlan: "draft",
    label: "Custom branding",
    description: "White-label PDFs and signer pages with your institution logo.",
  },
  "support.priority": {
    key: "support.priority",
    minPlan: "draft",
    label: "Priority support",
    description: "Same-day response from our support team.",
  },
  "auth.sso": {
    key: "auth.sso",
    minPlan: "enterprise",
    label: "SSO / SAML",
    description: "Bring your own identity provider for sign-in.",
  },
  "api.public": {
    key: "api.public",
    minPlan: "enterprise",
    label: "Public REST API",
    description: "Programmatic access to your Zindeal data.",
  },
};

// ---------------------------------------------------------------------------
// Plan limits
// ---------------------------------------------------------------------------

/**
 * Coach seat caps per plan.
 * For Track and Draft (per-seat plans), `null` here means the cap is
 * determined by the purchased seat quantity stored on the org.
 * Free is always capped at 5.
 */
export const PLAN_COACH_LIMITS: Record<Plan, number | null> = {
  free: 5,
  track: null,
  draft: null,
  enterprise: null,
};

/**
 * Returns the coach limit for a plan.
 * For per-seat plans (track/draft), returns null — callers should enforce
 * the purchased seat quantity from the org record instead.
 */
export function getCoachLimit(plan: Plan): number | null {
  return PLAN_COACH_LIMITS[plan] ?? null;
}

/**
 * Returns the effective coach seat cap for an org, taking purchased seats
 * into account for per-seat plans (track / draft).
 */
export function getEffectiveCoachLimit(plan: Plan, purchasedSeats: number): number | null {
  if (plan === "track" || plan === "draft") return Math.max(1, purchasedSeats);
  return getCoachLimit(plan);
}

// ---------------------------------------------------------------------------
// Comparisons
// ---------------------------------------------------------------------------

export function planRank(plan: Plan): number {
  const idx = PLAN_ORDER.indexOf(plan);
  return idx === -1 ? 0 : idx;
}

export function planMeetsRequirement(current: Plan, required: Plan): boolean {
  return planRank(current) >= planRank(required);
}

export function hasFeature(plan: Plan, feature: FeatureKey): boolean {
  const spec = FEATURES[feature];
  if (!spec) return false;
  return planMeetsRequirement(plan, spec.minPlan);
}

export function requiredPlanFor(feature: FeatureKey): Plan {
  return FEATURES[feature]?.minPlan ?? "enterprise";
}

export function nextPlan(plan: Plan): Plan {
  const idx = PLAN_ORDER.indexOf(plan);
  if (idx < 0 || idx >= PLAN_ORDER.length - 1) return "enterprise";
  return PLAN_ORDER[idx + 1]!;
}
